<template>
  <div class="cf-flex cf-flex--column cf-flex--gap-16">
    <div class="cf-flex cf-flex--column cf-flex--gap-12">
      <div class="cf-flex cf-flex--row cf-flex--align-center cf-flex--justify-space-between">
        <h3 class="cf-title cf-title--3">
          {{ titleHeader }}:
        </h3>

        <p class="cf-subtitle cf-subtitle--1 cf-text-italics">
          #{{ receipt.ticketNumber }}
        </p>
      </div>

      <!-- Group Order Items -->
      <div
        v-if="isGroupOrder"
        class="cf-flex cf-flex--column cf-flex--gap-16"
      >
        <div
          v-for="(member, index) in receipt.groupOrder.allMembers"
          :key="index"
          class="cf-flex cf-flex--column cf-flex--gap-16"
        >
          <div class="cf-flex cf-flex--row cf-flex--align-center cf-flex--justify-space-between">
            <div class="cf-flex cf-flex--row cf-flex--align-center cf-flex--gap-8">
              <div
                class="cf-user-avatar"
                :class="{ 'cf-user-avatar--brand-primary': checkIfMemberIsYou(member) }"
              >
                {{ member.displayNameInitials }}
              </div>

              <h4 class="cf-title cf-title--4 cf-align-self-center">
                {{ member.displayName }}

                <span v-if="checkIfMemberIsYou(member)">
                  (You):
                </span>

                <span v-else>
                  (Guest):
                </span>
              </h4>

              <p
                v-if="member.ticketNumber"
                class="cf-subtitle cf-subtitle--2"
              >
                #{{ member.ticketNumber }}
              </p>
            </div>
          </div>

          <cf-order-items
            v-if="member.orderItems.length > 0"
            :hide-prices="isItemPricingHidden"
            :disable-favoriting="true"
            :hide-totals="true"
            :hide-quantity="true"
            :order="receipt"
            :order-items="member.orderItems"
            :read-only="true"
            :is-your-order-item="checkIfMemberIsYou(member)"
            :is-item-rating-enabled="isItemRatingEnabled"
            :is-receipt-view="true"
          />

          <div
            v-if="!isItemPricingHidden"
            class="cf-flex cf-flex--row cf-flex--align-center cf-flex--justify-space-between cf-flex--gap-8"
          >
            <h4 class="cf-title cf-title--4 cf-title--gray">
              Subtotal
            </h4>

            <p class="cf-subtitle cf-subtitle--2 cf-subtitle--black cf-subtitle--heavy">
              {{ $dollars(member.subtotal) }}
            </p>
          </div>

          <cf-title-separator :omit-title="true" />
        </div>

        <cf-order-totals
          v-if="!isItemPricingHidden"
          :order="receipt"
          :totals="{ tip: tip, totalWithTip: totalWithTip }"
        />
      </div>

      <!-- Non-Group Order Items -->
      <div
        v-else
        class="cf-flex cf-flex--column cf-flex--gap-16"
      >
        <cf-order-items
          :hide-prices="isItemPricingHidden"
          :disable-favoriting="receipt.menuType === 'Catering'"
          :order="receipt"
          :read-only="true"
          :is-receipt-view="true"
          :is-item-rating-enabled="isItemRatingEnabled"
        />

        <cf-order-totals
          v-if="!isItemPricingHidden"
          :order="receipt"
          :totals="{ tip: tip, totalWithTip: totalWithTip }"
        />
      </div>
    </div>

    <!-- Order Level Instructions -->
    <template v-if="receipt.orderLevelInstructions">
      <cf-title-separator :omit-title="true" />

      <div class="cf-flex cf-flex--column cf-flex--gap-8">
        <h3 class="cf-title cf-title--3">
          {{ orderInstructionsContent }}:
        </h3>

        <p class="cf-subtitle cf-subtitle--2">
          {{ receipt.orderLevelInstructions }}
        </p>
      </div>
    </template>

    <!-- Save As Favorite -->
    <div
      v-if="isFavoritingEnabled"
      class="center"
    >
      <button
        v-if="favoriteOrderWasSaved"
        type="button"
        class="cf-button cf-button--secondary cf-button--lg cf-button--fill"
        @click="viewFavoritesClickHandler"
      >
        <span class="cf-icon cf-icon--red fa fa-heart" />

        View Favorites
      </button>

      <button
        v-else
        type="button"
        class="cf-button cf-button--secondary cf-button--lg cf-button--fill"
        @click="saveFavoriteOrderClickHandler"
      >
        <span class="cf-icon cf-icon--red fa fa-heart" />

        Save Order
      </button>
    </div>

    <cf-title-separator :omit-title="true" />
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';

  import CfTitleSeparator from '@/view/components/shared/cf-title-separator/index.vue';

  import CfOrderItems from '@/view/components/globals/cf-order-items/index.vue';
  import CfOrderTotals from '@/view/components/globals/cf-order-totals/index.vue';


  export default {
    name: 'ReceiptOrderItems',

    components: {
      CfTitleSeparator,
      CfOrderItems,
      CfOrderTotals
    },

    props: {
      receipt: {
        type: Object,
        required: true
      },

      isGuestReceipt: {
        type: Boolean,
        default: false
      },

      isFavoriteEnabled: {
        type: Boolean,
        default: true
      },

      isItemRatingEnabled: {
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        email: null,
        favoriteOrderWasSaved: false
      };
    },

    computed: {
      ...mapGetters('content', ['content']),
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('session', ['currentUser']),

      isGroupOrder() {
        return this.receipt.groupOrder?.token;
      },

      titleHeader() {
        return this.isGroupOrder
          ? 'Your Group Order'
          : 'Your Order';
      },

      isItemPricingHidden() {
        return this.receipt.store?.features?.HidePrices;
      },

      isFavoritingEnabled() {
        return this.isFavoriteEnabled && this.currentUser.accountId && !this.isGuestReceipt && this.receipt.menuType !== 'Catering' && !this.merchant.features['hide-favorites'];
      },

      tip() {
        return this.receipt.tip;
      },

      totalWithTip() {
        const { totalWithTipAndCharges } = this.receipt;

        return totalWithTipAndCharges;
      },

      orderInstructionsContent() {
        return this.content.OrderLevelInstructions.metadata.inputPlaceholder;
      }
    },

    methods: {
      saveFavoriteOrderClickHandler() {
        this.$openModal('editFavoriteOrder', {
          order: this.receipt,
          onSave: this.favoriteOrderWasSavedHandler
        });
      },

      favoriteOrderWasSavedHandler() {
        this.favoriteOrderWasSaved = true;
      },

      checkIfMemberIsYou(member) {
        const isGuestReceipt = !!this.$route.query.guestId;
        const hostMemberIsYou = member.isHost && !isGuestReceipt;
        const guestMemberIsYou = isGuestReceipt && member.guestId === this.$route.query.guestId;

        return hostMemberIsYou || guestMemberIsYou;
      },

      viewFavoritesClickHandler() {
        this.$router.push({
          name: 'orderFavorites',
          params: {
            merchantId: this.$route.params.merchantId
          }
        });
      }
    }
  };
</script>
